import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Tabs,
  Tab,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  MenuItem,
} from '@mui/material';
import {
  Receipt,
  Assessment,
  AccountBalanceWallet,
  Refresh,
  Warning,
} from '@mui/icons-material';
import { financeService } from '../../services/financeService';
import {
  TDSSummary,
  TDSQuarterlyData,
  TDSPayableReport,
  TDSChallan,
} from '../../types/finance';
import {
  ReportHeader,
  ExportToolbar,
  IndianAmountDisplay,
} from '../../components/finance';
import { exportToPDF, exportToExcel, formatIndianCurrency, formatDate } from '../../utils/exportUtils';
import { useCompanySettings, formatCompanyAddress } from '../../hooks/useCompanySettings';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  if (value !== index) return null;
  return (
    <Box role="tabpanel" sx={{ py: 3 }}>
      {children}
    </Box>
  );
};

const TDSReportsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const company = useCompanySettings();
  const companyName = company?.companyName || 'Your Company Name';
  const companyGstin = company?.gstin || undefined;
  const companyAddress = formatCompanyAddress(company) || undefined;

  // Financial Year options
  const getFinancialYearOptions = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const fyStartYear = currentMonth >= 4 ? currentYear : currentYear - 1;

    const options = [];
    for (let i = 0; i < 3; i++) {
      const startYear = fyStartYear - i;
      options.push({
        value: `FY${startYear}-${(startYear + 1).toString().slice(-2)}`,
        label: `FY ${startYear}-${(startYear + 1).toString().slice(-2)}`,
      });
    }
    return options;
  };

  const fyOptions = getFinancialYearOptions();
  const [financialYear, setFinancialYear] = useState(fyOptions[0].value);
  const [quarter, setQuarter] = useState<'Q1' | 'Q2' | 'Q3' | 'Q4'>('Q1');

  // Report States
  const [tdsSummary, setTdsSummary] = useState<TDSSummary | null>(null);
  const [tdsQuarterly, setTdsQuarterly] = useState<TDSQuarterlyData | null>(null);
  const [tdsPayable, setTdsPayable] = useState<TDSPayableReport | null>(null);
  const [challans, setChallans] = useState<TDSChallan[]>([]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
  };

  const fetchTDSSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await financeService.getTDSSummary(financialYear);
      setTdsSummary(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load TDS summary');
    } finally {
      setLoading(false);
    }
  };

  const fetchTDSQuarterly = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await financeService.getTDSQuarterlyData(financialYear, quarter);
      setTdsQuarterly(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load TDS quarterly data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTDSPayable = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await financeService.getTDSPayableReport(financialYear, quarter);
      setTdsPayable(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load TDS payable report');
    } finally {
      setLoading(false);
    }
  };

  const fetchChallans = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await financeService.getTDSChallanList(financialYear);
      setChallans(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load challans');
    } finally {
      setLoading(false);
    }
  };

  const quarterLabels: Record<string, string> = {
    Q1: 'Apr - Jun',
    Q2: 'Jul - Sep',
    Q3: 'Oct - Dec',
    Q4: 'Jan - Mar',
  };

  // Export handlers for TDS Summary
  const handleTDSSummaryPDF = () => {
    exportToPDF('tds-summary-report', `TDS_Summary_${financialYear}`, 'landscape');
  };

  const handleTDSSummaryExcel = () => {
    if (!tdsSummary) return;
    const data = tdsSummary.sections.map((section) => ({
      section: section.section,
      description: section.sectionDescription,
      count: section.deductionCount,
      paymentAmount: formatIndianCurrency(section.totalPaymentAmount),
      tdsDeducted: formatIndianCurrency(section.totalTDSDeducted),
      deposited: formatIndianCurrency(section.totalDeposited),
      pending: formatIndianCurrency(section.totalPending),
    }));
    const columns = [
      { header: 'Section', key: 'section' },
      { header: 'Description', key: 'description' },
      { header: 'Count', key: 'count' },
      { header: 'Payment Amount', key: 'paymentAmount' },
      { header: 'TDS Deducted', key: 'tdsDeducted' },
      { header: 'Deposited', key: 'deposited' },
      { header: 'Pending', key: 'pending' },
    ];
    exportToExcel(data, columns, `TDS_Summary_${financialYear}`, 'TDS Summary');
  };

  // Export handlers for TDS Quarterly
  const handleTDSQuarterlyPDF = () => {
    exportToPDF('tds-quarterly-report', `TDS_26Q_${financialYear}_${quarter}`, 'landscape');
  };

  const handleTDSQuarterlyExcel = () => {
    if (!tdsQuarterly) return;
    const data = tdsQuarterly.deductions.map((d) => ({
      deducteeName: d.deductee_name,
      pan: d.deductee_pan,
      section: d.tds_section,
      paymentDate: formatDate(d.payment_date),
      paymentAmount: formatIndianCurrency(d.payment_amount),
      tdsRate: `${d.tds_rate}%`,
      tdsAmount: formatIndianCurrency(d.total_tds),
    }));
    const columns = [
      { header: 'Deductee Name', key: 'deducteeName' },
      { header: 'PAN', key: 'pan' },
      { header: 'Section', key: 'section' },
      { header: 'Payment Date', key: 'paymentDate' },
      { header: 'Payment Amount', key: 'paymentAmount' },
      { header: 'TDS Rate', key: 'tdsRate' },
      { header: 'TDS Amount', key: 'tdsAmount' },
    ];
    exportToExcel(data, columns, `TDS_26Q_${financialYear}_${quarter}`, 'Form 26Q');
  };

  // Export handlers for TDS Payable
  const handleTDSPayablePDF = () => {
    exportToPDF('tds-payable-report', `TDS_Payable_${financialYear}_${quarter}`, 'landscape');
  };

  const handleTDSPayableExcel = () => {
    if (!tdsPayable) return;
    const data = tdsPayable.items.map((item) => ({
      section: item.section,
      deducteeName: item.deducteeName,
      pan: item.deducteePan,
      paymentDate: formatDate(item.paymentDate),
      tdsAmount: formatIndianCurrency(item.tdsAmount),
      dueDate: item.depositDueDate,
      daysOverdue: item.daysOverdue,
      interest: formatIndianCurrency(item.interestPayable),
    }));
    const columns = [
      { header: 'Section', key: 'section' },
      { header: 'Deductee Name', key: 'deducteeName' },
      { header: 'PAN', key: 'pan' },
      { header: 'Payment Date', key: 'paymentDate' },
      { header: 'TDS Amount', key: 'tdsAmount' },
      { header: 'Due Date', key: 'dueDate' },
      { header: 'Days Overdue', key: 'daysOverdue' },
      { header: 'Interest', key: 'interest' },
    ];
    exportToExcel(data, columns, `TDS_Payable_${financialYear}_${quarter}`, 'TDS Payable');
  };

  // Export handlers for Challans
  const handleChallansPDF = () => {
    exportToPDF('tds-challans-report', `TDS_Challans_${financialYear}`, 'landscape');
  };

  const handleChallansExcel = () => {
    if (!challans || challans.length === 0) return;
    const data = challans.map((challan) => ({
      challanNumber: challan.challan_number,
      bsrCode: challan.bsr_code,
      depositDate: formatDate(challan.deposit_date),
      bankName: challan.bank_name,
      quarter: challan.quarter,
      amount: formatIndianCurrency(challan.deposit_amount),
    }));
    const columns = [
      { header: 'Challan No.', key: 'challanNumber' },
      { header: 'BSR Code', key: 'bsrCode' },
      { header: 'Deposit Date', key: 'depositDate' },
      { header: 'Bank Name', key: 'bankName' },
      { header: 'Quarter', key: 'quarter' },
      { header: 'Amount', key: 'amount' },
    ];
    exportToExcel(data, columns, `TDS_Challans_${financialYear}`, 'Challans');
  };

  return (
    <>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
          TDS Reports
        </Typography>
        <Typography variant="body2" color="text.secondary">
          TDS Summary, Quarterly Returns (26Q/27Q), Payable and Challan tracking
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Assessment />} label="Summary" iconPosition="start" />
          <Tab icon={<Receipt />} label="Quarterly (26Q)" iconPosition="start" />
          <Tab icon={<Warning />} label="Payable" iconPosition="start" />
          <Tab icon={<AccountBalanceWallet />} label="Challans" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Tab */}
      <TabPanel value={tabValue} index={0}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                select
                label="Financial Year"
                size="small"
                fullWidth
                value={financialYear}
                onChange={(e) => setFinancialYear(e.target.value)}
              >
                {fyOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Button
                variant="contained"
                onClick={fetchTDSSummary}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
              >
                Generate Summary
              </Button>
            </Grid>
            {tdsSummary && (
              <Grid size={{ xs: 12, sm: 4 }} sx={{ textAlign: 'right' }}>
                <ExportToolbar
                  onExportPDF={handleTDSSummaryPDF}
                  onExportExcel={handleTDSSummaryExcel}
                  disabled={loading}
                  size="small"
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {loading && tabValue === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : tdsSummary ? (
          <Box id="tds-summary-report">
            <ReportHeader
              companyName={companyName}
              gstin={companyGstin}
              address={companyAddress}
              reportTitle="TDS Summary by Section"
              subtitle={`Financial Year: ${tdsSummary.financialYear}`}
            />

            {/* Totals Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">Total Deductions</Typography>
                    <Typography variant="h5">{tdsSummary.totals.totalDeductions}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">TDS Deducted</Typography>
                    <IndianAmountDisplay amount={tdsSummary.totals.totalTDSDeducted} bold size="large" />
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Card sx={{ bgcolor: 'success.50' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">TDS Deposited</Typography>
                    <IndianAmountDisplay amount={tdsSummary.totals.totalDeposited} bold size="large" color="positive" />
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Card sx={{ bgcolor: tdsSummary.totals.totalPending > 0 ? 'error.50' : 'grey.100' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">TDS Pending</Typography>
                    <IndianAmountDisplay
                      amount={tdsSummary.totals.totalPending}
                      bold
                      size="large"
                      color={tdsSummary.totals.totalPending > 0 ? 'negative' : 'positive'}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Section-wise Table */}
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Section</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Count</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Payment Amount</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>TDS Deducted</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Deposited</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Pending</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tdsSummary.sections.map((section, index) => (
                    <TableRow key={index} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                      <TableCell sx={{ fontWeight: 500 }}>{section.section}</TableCell>
                      <TableCell>{section.sectionDescription}</TableCell>
                      <TableCell align="center">{section.deductionCount}</TableCell>
                      <TableCell align="right">
                        <IndianAmountDisplay amount={section.totalPaymentAmount} />
                      </TableCell>
                      <TableCell align="right">
                        <IndianAmountDisplay amount={section.totalTDSDeducted} />
                      </TableCell>
                      <TableCell align="right">
                        <IndianAmountDisplay amount={section.totalDeposited} color="positive" />
                      </TableCell>
                      <TableCell align="right">
                        <IndianAmountDisplay
                          amount={section.totalPending}
                          color={section.totalPending > 0 ? 'negative' : 'neutral'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Assessment sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Select a financial year and click Generate to view TDS Summary
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Quarterly Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                select
                label="Financial Year"
                size="small"
                fullWidth
                value={financialYear}
                onChange={(e) => setFinancialYear(e.target.value)}
              >
                {fyOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                select
                label="Quarter"
                size="small"
                fullWidth
                value={quarter}
                onChange={(e) => setQuarter(e.target.value as any)}
              >
                <MenuItem value="Q1">Q1 (Apr - Jun)</MenuItem>
                <MenuItem value="Q2">Q2 (Jul - Sep)</MenuItem>
                <MenuItem value="Q3">Q3 (Oct - Dec)</MenuItem>
                <MenuItem value="Q4">Q4 (Jan - Mar)</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Button
                variant="contained"
                onClick={fetchTDSQuarterly}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
              >
                Generate 26Q Data
              </Button>
            </Grid>
            {tdsQuarterly && (
              <Grid size={{ xs: 12, sm: 3 }} sx={{ textAlign: 'right' }}>
                <ExportToolbar
                  onExportPDF={handleTDSQuarterlyPDF}
                  onExportExcel={handleTDSQuarterlyExcel}
                  disabled={loading}
                  size="small"
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {loading && tabValue === 1 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : tdsQuarterly ? (
          <Box id="tds-quarterly-report">
            <ReportHeader
              companyName={companyName}
              gstin={companyGstin}
              address={companyAddress}
              reportTitle={`Form 26Q - Quarter ${tdsQuarterly.quarter}`}
              subtitle={`${tdsQuarterly.financialYear} | ${quarterLabels[tdsQuarterly.quarter]}`}
            />

            {/* Due Dates Alert */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Due Dates:</strong> TDS Deposit: {tdsQuarterly.depositDueDate} |
              Return Filing: {tdsQuarterly.dueDate}
            </Alert>

            {/* Summary */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">TDS Deducted</Typography>
                    <IndianAmountDisplay amount={tdsQuarterly.summary.totalTDSDeducted} bold />
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">TDS Deposited</Typography>
                    <IndianAmountDisplay amount={tdsQuarterly.summary.totalTDSDeposited} bold color="positive" />
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">Interest</Typography>
                    <IndianAmountDisplay amount={tdsQuarterly.summary.interest} bold color="negative" />
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">Late Fee</Typography>
                    <IndianAmountDisplay amount={tdsQuarterly.summary.lateFee} bold color="negative" />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Deductions Table */}
            <Paper sx={{ mb: 3 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Deduction Details
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>Deductee Name</TableCell>
                      <TableCell>PAN</TableCell>
                      <TableCell>Section</TableCell>
                      <TableCell>Payment Date</TableCell>
                      <TableCell align="right">Payment Amount</TableCell>
                      <TableCell align="right">TDS Rate</TableCell>
                      <TableCell align="right">TDS Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tdsQuarterly.deductions.map((d, index) => (
                      <TableRow key={index}>
                        <TableCell>{d.deductee_name}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{d.deductee_pan}</TableCell>
                        <TableCell>{d.tds_section}</TableCell>
                        <TableCell>{new Date(d.payment_date).toLocaleDateString('en-IN')}</TableCell>
                        <TableCell align="right">
                          <IndianAmountDisplay amount={d.payment_amount} />
                        </TableCell>
                        <TableCell align="right">{d.tds_rate}%</TableCell>
                        <TableCell align="right">
                          <IndianAmountDisplay amount={d.total_tds} bold />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        ) : (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Receipt sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Select FY and Quarter to generate 26Q data
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Payable Tab */}
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                select
                label="Financial Year"
                size="small"
                fullWidth
                value={financialYear}
                onChange={(e) => setFinancialYear(e.target.value)}
              >
                {fyOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                select
                label="Quarter"
                size="small"
                fullWidth
                value={quarter}
                onChange={(e) => setQuarter(e.target.value as any)}
              >
                <MenuItem value="Q1">Q1 (Apr - Jun)</MenuItem>
                <MenuItem value="Q2">Q2 (Jul - Sep)</MenuItem>
                <MenuItem value="Q3">Q3 (Oct - Dec)</MenuItem>
                <MenuItem value="Q4">Q4 (Jan - Mar)</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Button
                variant="contained"
                onClick={fetchTDSPayable}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
              >
                Generate Payable Report
              </Button>
            </Grid>
            {tdsPayable && (
              <Grid size={{ xs: 12, sm: 3 }} sx={{ textAlign: 'right' }}>
                <ExportToolbar
                  onExportPDF={handleTDSPayablePDF}
                  onExportExcel={handleTDSPayableExcel}
                  disabled={loading}
                  size="small"
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {loading && tabValue === 2 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : tdsPayable ? (
          <Box id="tds-payable-report">
            <ReportHeader
              companyName={companyName}
              gstin={companyGstin}
              address={companyAddress}
              reportTitle="TDS Payable Report"
              subtitle={`${tdsPayable.financialYear} - ${quarterLabels[tdsPayable.quarter]}`}
            />

            {tdsPayable.totals.totalTDSPayable > 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <strong>Pending TDS:</strong> Total {' '}
                <IndianAmountDisplay amount={tdsPayable.totals.totalTDSPayable} bold /> pending deposit.
                Interest payable: <IndianAmountDisplay amount={tdsPayable.totals.totalInterest} bold color="negative" />
              </Alert>
            )}

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'warning.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Section</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Deductee</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>PAN</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Payment Date</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>TDS Amount</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Due Date</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Days Overdue</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Interest</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tdsPayable.items.map((item, index) => (
                    <TableRow key={index} sx={{ bgcolor: item.daysOverdue > 0 ? 'error.50' : undefined }}>
                      <TableCell>{item.section}</TableCell>
                      <TableCell>{item.deducteeName}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{item.deducteePan}</TableCell>
                      <TableCell>{new Date(item.paymentDate).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell align="right">
                        <IndianAmountDisplay amount={item.tdsAmount} />
                      </TableCell>
                      <TableCell>{item.depositDueDate}</TableCell>
                      <TableCell align="center">
                        {item.daysOverdue > 0 ? (
                          <Chip label={`${item.daysOverdue} days`} size="small" color="error" />
                        ) : (
                          <Chip label="On Time" size="small" color="success" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IndianAmountDisplay amount={item.interestPayable} color="negative" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Warning sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Select FY and Quarter to view pending TDS
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Challans Tab */}
      <TabPanel value={tabValue} index={3}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                select
                label="Financial Year"
                size="small"
                fullWidth
                value={financialYear}
                onChange={(e) => setFinancialYear(e.target.value)}
              >
                {fyOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Button
                variant="contained"
                onClick={fetchChallans}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
              >
                Load Challans
              </Button>
            </Grid>
            {challans.length > 0 && (
              <Grid size={{ xs: 12, sm: 4 }} sx={{ textAlign: 'right' }}>
                <ExportToolbar
                  onExportPDF={handleChallansPDF}
                  onExportExcel={handleChallansExcel}
                  disabled={loading}
                  size="small"
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {loading && tabValue === 3 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : challans.length > 0 ? (
          <Box id="tds-challans-report">
            <ReportHeader
              companyName={companyName}
              gstin={companyGstin}
              address={companyAddress}
              reportTitle="TDS Challan List"
              subtitle={`Financial Year: ${financialYear}`}
            />

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Challan No.</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>BSR Code</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Deposit Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Bank</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Quarter</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {challans.map((challan, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                        {challan.challan_number}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{challan.bsr_code}</TableCell>
                      <TableCell>{new Date(challan.deposit_date).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell>{challan.bank_name}</TableCell>
                      <TableCell>
                        <Chip label={challan.quarter} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <IndianAmountDisplay amount={challan.deposit_amount} bold />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <AccountBalanceWallet sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Select a financial year and click Load to view challans
            </Typography>
          </Paper>
        )}
      </TabPanel>
    </>
  );
};

export default TDSReportsPage;
